// Evidence tag management page (FRMW-005).
// T3 config screen — simple CRUD list of evidence tags.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { evidenceTagsApi, type EvidenceTag } from '@/lib/api/frameworkConfig'
import { evidenceTagSchema, type EvidenceTagFormData } from '@/lib/schemas/frameworkConfig'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { useToast } from '@/lib/hooks/use-toast'
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react'

type DialogMode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; tag: EvidenceTag }
  | { kind: 'delete'; tag: EvidenceTag }

export function EvidenceTagsPage() {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const tagApi = evidenceTagsApi(api)

  const [dialog, setDialog] = useState<DialogMode>({ kind: 'closed' })

  const {
    data: tags,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.frameworkConfig.evidenceTags(slug),
    queryFn: () => tagApi.list(),
  })

  // --- Create ---
  const createMutation = useMutation({
    mutationFn: (data: EvidenceTagFormData) =>
      tagApi.create({
        tag_name: data.tag_name,
        description: data.description || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.evidenceTags(slug) })
      setDialog({ kind: 'closed' })
      toast({ title: t('evidenceTags.created') })
    },
    onError: (err) => {
      toast({
        title: t('evidenceTags.createFailed'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  // --- Update ---
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: EvidenceTagFormData }) =>
      tagApi.update(id, {
        tag_name: data.tag_name,
        description: data.description || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.evidenceTags(slug) })
      setDialog({ kind: 'closed' })
      toast({ title: t('evidenceTags.updated') })
    },
    onError: (err) => {
      toast({
        title: t('evidenceTags.updateFailed'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  // --- Delete ---
  const deleteMutation = useMutation({
    mutationFn: (id: string) => tagApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.evidenceTags(slug) })
      setDialog({ kind: 'closed' })
      toast({ title: t('evidenceTags.deleted') })
    },
    onError: (err) => {
      toast({
        title: t('evidenceTags.deleteFailed'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return <TableSkeleton />
  }

  if (error) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('evidenceTags.title') })}
        technicalDetail={error instanceof ApiRequestError ? error.message : error.message}
        onRetry={() => refetch()}
      />
    )
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('evidenceTags.title')}</h1>
          <p className="text-muted-foreground">{t('evidenceTags.description')}</p>
        </div>
        <Button onClick={() => setDialog({ kind: 'create' })}>
          <Plus className="mr-2 h-4 w-4" />
          {t('evidenceTags.addTag')}
        </Button>
      </div>

      {/* Tag list */}
      {!tags || tags.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('evidenceTags.noTags')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tags.map((tag) => (
            <Card key={tag.id}>
              <CardContent className="flex items-center justify-between py-4">
                <div>
                  <p className="font-medium">{tag.tag_name}</p>
                  {tag.description && (
                    <p className="text-sm text-muted-foreground">{tag.description}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDialog({ kind: 'edit', tag })}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDialog({ kind: 'delete', tag })}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit dialog */}
      {(dialog.kind === 'create' || dialog.kind === 'edit') && (
        <TagFormDialog
          mode={dialog.kind}
          tag={dialog.kind === 'edit' ? dialog.tag : undefined}
          isPending={createMutation.isPending || updateMutation.isPending}
          onSubmit={(data) => {
            if (dialog.kind === 'create') {
              createMutation.mutate(data)
            } else {
              updateMutation.mutate({ id: dialog.tag.id, data })
            }
          }}
          onCancel={() => setDialog({ kind: 'closed' })}
        />
      )}

      {/* Delete confirmation dialog */}
      {dialog.kind === 'delete' && (
        <DeleteConfirmDialog
          tagName={dialog.tag.tag_name}
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(dialog.tag.id)}
          onCancel={() => setDialog({ kind: 'closed' })}
        />
      )}
    </div>
  )
}

// --- Tag form dialog (create/edit) ---

interface TagFormDialogProps {
  mode: 'create' | 'edit'
  tag?: EvidenceTag
  isPending: boolean
  onSubmit: (data: EvidenceTagFormData) => void
  onCancel: () => void
}

function TagFormDialog({ mode, tag, isPending, onSubmit, onCancel }: TagFormDialogProps) {
  const { t } = useTranslation(['frameworkConfig', 'common'])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EvidenceTagFormData>({
    resolver: zodResolver(evidenceTagSchema),
    defaultValues: {
      tag_name: tag?.tag_name ?? '',
      description: tag?.description ?? '',
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <Card className="relative z-10 mx-4 w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {mode === 'create' ? t('evidenceTags.addTag') : t('evidenceTags.editTag')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tag_name">{t('evidenceTags.tagName')}</Label>
              <Input id="tag_name" autoFocus {...register('tag_name')} />
              {errors.tag_name && (
                <p className="text-sm text-destructive">{errors.tag_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('evidenceTags.tagDescription')}</Label>
              <Input id="description" {...register('description')} />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common:saving')}
                  </>
                ) : mode === 'create' ? (
                  t('common:create')
                ) : (
                  t('common:save')
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

// --- Delete confirmation dialog ---

interface DeleteConfirmDialogProps {
  tagName: string
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}

function DeleteConfirmDialog({
  tagName,
  isPending,
  onConfirm,
  onCancel,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation(['frameworkConfig', 'common'])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <Card className="relative z-10 mx-4 w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('evidenceTags.deleteTag')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('evidenceTags.deleteConfirm', { name: tagName })}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel} disabled={isPending}>
              {t('common:cancel')}
            </Button>
            <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common:delete')}
                </>
              ) : (
                t('common:delete')
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
